// ============================================
// JSONB NORMALIZE - legacy-record healing helper
// ============================================
// Background:
//   Before the `OPAQUE_JSONB_KEYS` allowlist was added to
//   `src/services/data-service-api.js` (2026-04-20), the response-side
//   `convertKeysToCamel` function recursed into opaque JSONB blobs authored
//   by AI prompts and silently rewrote their INTERNAL snake_case keys to
//   camelCase.
//
//   The allowlist stops the damage for NEW saves, but every project that
//   was saved while the bug was live still carries camelCased internal
//   keys on disk (Dexie + Postgres). When such a record is loaded, Stage
//   2 Risk Scan reads `analysis.detected_risks` and gets `undefined`
//   because the field on disk is spelled `detectedRisks`.
//
// Fix strategy:
//   Heal-on-read. When `completeProjectLoad` restores SPECIFIC blobs to
//   React state, pass them through `normalizeToSnake` first. The
//   function is a ONE-WAY, idempotent camelCase → snake_case walk:
//     - Strings, numbers, booleans, null, Date: passed through untouched.
//     - Arrays: mapped element-wise (order preserved).
//     - Objects: every key is snake-cased; values are recursed.
//     - Keys already in snake_case have no uppercase letters, so the
//       regex is a no-op on them - safe to run on fresh records.
//
//   This is deliberately NOT a save-side mutation: we don't rewrite
//   on-disk payloads. If a user never reloads a legacy project, nothing
//   changes. If they do, the next save after load will persist the
//   healed snake_case form naturally.
//
// Scope - which blobs this helper is appropriate for:
//   ✓ Pure AI-authored blobs whose keys are contracted as snake_case:
//       riskAnalysis, photoAnalysis, projectStrategy, costingSheet,
//       projectSummary.
//   ✗ Code-authored / mixed-casing blobs (DO NOT run through this):
//       wms         - activities carry `costBreakdown`,
//                     `costBreakdownLocked`, materials carry `unitRate`
//                     (camelCase, 224+ reads in src).
//       quoteData   - `header.heroImage`, `meta.quoteNumber`,
//                     `customSignature`, `isGstInclusive`, etc.
//       appliedLearnings - canonical shape is camelCase
//                     (`appliedAt` / `appliedBy` / `contradictReason`
//                     per utils/applied-learnings.js).
//       aiGeneratedItems, originalAiScope - scope-item snapshots whose
//                     items use camelCase fields.
//
//   The distinction: a blob is safe to normalise ONLY if EVERY internal
//   key is supposed to be snake_case. Mixed blobs need per-field healing
//   (not attempted here - they weren't affected by the original bug in
//   practice because code-authored camelCase keys round-tripped through
//   `convertKeysToCamel` unchanged).

function toSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Recursively convert any camelCase object keys in the given tree into
 * snake_case. Preserves arrays, Date instances, nulls, and non-object
 * leaf values (strings, numbers, booleans) without modification.
 *
 * Idempotent: keys that are already snake_case (no uppercase letters)
 * pass through unchanged, so this is safe to run on fresh records that
 * were saved under the post-allowlist code path.
 *
 * ONLY apply to pure-AI-authored blobs (see header comment for the
 * allow / deny list). Running this on mixed-casing blobs will damage
 * code-authored camelCase fields that consumers read verbatim.
 *
 * @param obj - any value; only plain objects and arrays are walked.
 * @returns the same shape with snake_cased keys.
 */
export function normalizeToSnake(obj: unknown): unknown {
    return mapKeys(obj, toSnakeCase);
}

function mapKeys(obj: unknown, mapKey: (key: string) => string): unknown {
    if (Array.isArray(obj)) return obj.map(item => mapKeys(item, mapKey));
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) return obj;
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        result[mapKey(key)] = mapKeys(value, mapKey);
    }
    return result;
}

// The old response converter rewrote `_x` to `X` in every key of the tree
// (data-service-api.js toCamelCase, /_([a-z])/g), so a blob it damaged has no
// key left that matches this anywhere.
const SNAKE_CASE_KEY = /_[a-z]/;

function hasSnakeCaseKey(obj: unknown): boolean {
    if (Array.isArray(obj)) return obj.some(hasSnakeCaseKey);
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) return false;
    for (const [key, value] of Object.entries(obj)) {
        if (SNAKE_CASE_KEY.test(key) || hasSnakeCaseKey(value)) return true;
    }
    return false;
}

/**
 * Heal-on-load for the AI blobs `completeProjectLoad` restores (riskAnalysis,
 * photoAnalysis, projectSummary, projectStrategy).
 *
 * A blob the old converter damaged (no snake_case key anywhere) is healed with
 * `normalizeToSnake`. Any other blob is returned as an unrenamed copy, because
 * current writers put code-authored camelCase keys in these blobs on purpose:
 * Stage 2's `riskEvidenceFingerprint` and `_reportSynthesisUsed`, Stage 1.5's
 * `summaryEvidenceFingerprint`, `generatedAt` and `deepContextStats`, the photo
 * forensic counters (`batchCount`, `providerRequestCount`, ...), the strategy's
 * `fallbackReason` and the nested `locality` fields. Those blobs are hashed
 * key-for-key by the Stage 2 and Stage 3 receipts, so renaming them on load
 * made the next save change the stored inputs (2026-10-04 UAT: a Quote Run
 * resumed at Stage 3 failed with WMS_SOURCE_INPUT_CHANGED). The receipt
 * readers accept both spellings, so a row renamed by an earlier load still
 * reads correctly, and readers of the camelCase spelling only (for example
 * ProjectStrategyPanel's `fallbackReason`) now see it after a reload too.
 */
export function healLegacyCamelCasedBlob(obj: unknown): unknown {
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) return obj;
    return hasSnakeCaseKey(obj) ? mapKeys(obj, key => key) : normalizeToSnake(obj);
}
